import React from 'react';

/**
 * Subtle, premium row of "Accepted Payment Methods" badges.
 *
 * Designed to sit just above the payment-method picker in the checkout sheet.
 * Goals:
 *   - signal trust at the exact moment of payment decision
 *   - feel authoritative, not cluttery
 *   - match NoFee's understated luxury feel (no neon, no cartoon)
 *
 * Each badge is a tiny rounded pill with the brand wordmark in its official
 * color but at moderate saturation, so the strip reads as a calm trust signal
 * rather than a marketing banner.
 */

const Badge = ({ children, testid, label, bg = 'bg-slate-50' }) => (
  <span
    data-testid={testid}
    aria-label={label}
    title={label}
    className={`inline-flex items-center justify-center h-7 min-w-[40px] px-2 rounded-md border border-slate-200 ${bg} text-[10.5px] font-bold tracking-tight select-none`}
  >
    {children}
  </span>
);

const PaymentTrustBadges = ({ className = '' }) => {
  return (
    <div
      data-testid="payment-trust-badges"
      className={`flex items-center justify-center gap-1.5 flex-wrap py-2.5 ${className}`}
    >
      <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold mr-1">
        Accepted
      </span>

      {/* Visa — blue */}
      <Badge testid="badge-visa" label="Visa">
        <span className="text-[#1A1F71] italic">VISA</span>
      </Badge>

      {/* Mastercard — red+orange circles */}
      <Badge testid="badge-mastercard" label="Mastercard">
        <span className="relative inline-block w-[26px] h-3.5">
          <span className="absolute left-0 top-0 w-3.5 h-3.5 rounded-full bg-[#EB001B] opacity-90" />
          <span className="absolute right-0 top-0 w-3.5 h-3.5 rounded-full bg-[#F79E1B] opacity-90 mix-blend-multiply" />
        </span>
      </Badge>

      {/* American Express */}
      <Badge testid="badge-amex" label="American Express" bg="bg-[#006FCF]">
        <span className="text-white text-[9px]">AMEX</span>
      </Badge>

      {/* Apple Pay */}
      <Badge testid="badge-apple-pay" label="Apple Pay" bg="bg-black">
        <span className="inline-flex items-center text-white tracking-tight">
          <svg className="w-3 h-3 mr-0.5" viewBox="0 0 17 21" fill="white" aria-hidden="true">
            <path d="M14.07 11.18c-.02-2.22 1.81-3.29 1.9-3.34-1.04-1.51-2.65-1.73-3.22-1.76-1.37-.14-2.67.81-3.36.81-.7 0-1.77-.79-2.91-.77-1.49.02-2.87.87-3.64 2.2-1.56 2.7-.4 6.7 1.11 8.9.75 1.08 1.63 2.28 2.79 2.24 1.13-.04 1.55-.72 2.91-.72s1.74.72 2.92.7c1.21-.02 1.97-1.08 2.7-2.18.85-1.24 1.21-2.46 1.22-2.52-.03-.01-2.36-.91-2.42-3.57zM11.46 4.5c.62-.75 1.04-1.79.92-2.83-.9.04-1.99.6-2.63 1.35-.57.66-1.07 1.73-.94 2.74 1 .07 2.02-.51 2.65-1.26z"/>
          </svg>
          Pay
        </span>
      </Badge>

      {/* Google Pay */}
      <Badge testid="badge-google-pay" label="Google Pay">
        <span>
          <span className="text-[#4285F4]">G</span>
          <span className="text-[#EA4335]">o</span>
          <span className="text-[#FBBC05]">o</span>
          <span className="text-[#4285F4]">g</span>
          <span className="text-[#34A853]">l</span>
          <span className="text-[#EA4335]">e</span>
          <span className="text-slate-700"> Pay</span>
        </span>
      </Badge>
    </div>
  );
};

export default PaymentTrustBadges;
